import { TimestampService } from '@zlinkx/shared-v2';
import * as Sentry from '@sentry/react';

export const OBSERVABILITY_EVENTS = {
	ISSUE_FRONTEND_UNHANDLED: 'frontend_unhandled_exception',
	ISSUE_FORCED_LOGOUT_LOOP: 'forced_logout_loop_detected',
	LOG_API_REQUEST_FAILED: 'api_request_failed',
	LOG_API_401_RECEIVED: 'api_401_received',
	LOG_FORCED_LOGOUT_TRIGGERED: 'forced_logout_triggered',
	LOG_AUTH_STATE_CHANGED: 'auth_state_changed',
	LOG_AUTH_TOKEN_REFRESHED: 'auth_token_refreshed',
	LOG_AUTH_REFRESH_FAILED: 'auth_refresh_failed',
	LOG_STORAGE_PROBE_FAILED: 'storage_probe_failed',
} as const;

type ObservabilityEventName = typeof OBSERVABILITY_EVENTS[keyof typeof OBSERVABILITY_EVENTS];

type ObservabilityValue = string | number | boolean | null;
type ObservabilityPayload = Record<string, ObservabilityValue | ObservabilityValue[] | undefined>;
type ScopeLike = {
	setTag?: (key: string, value: string) => void;
	setExtra?: (key: string, value: unknown) => void;
	setLevel?: (level: Sentry.SeverityLevel) => void;
};

const DEDUPE_WINDOW_MS = 60_000;
const dedupeState = new Map<string, number>();

function shouldDedupe(key: string): boolean {
	const now = TimestampService.now();
	const previous = dedupeState.get(key);

	if (previous !== undefined && now - previous < DEDUPE_WINDOW_MS) {
		return true;
	}

	dedupeState.set(key, now);

	// Best effort memory cap for long-lived browser tabs.
	if (dedupeState.size > 250) {
		const oldestKey = dedupeState.keys().next().value;
		if (typeof oldestKey === 'string') {
			dedupeState.delete(oldestKey);
		}
	}

	return false;
}

function applyPayload(scope: ScopeLike, payload: ObservabilityPayload): void {
	for (const [key, value] of Object.entries(payload)) {
		if (value !== undefined) {
			scope.setExtra?.(key, value);
		}
	}
}

function inferLogLevel(status: number | undefined): Sentry.SeverityLevel {
	if (typeof status !== 'number') {
		return 'warning';
	}

	if (status >= 500) {
		return 'error';
	}

	if (status >= 400) {
		return 'warning';
	}

	return 'info';
}

export function captureStructuredLog(
	eventName: ObservabilityEventName,
	payload: ObservabilityPayload,
	options?: {
		dedupeKey?: string;
		level?: Sentry.SeverityLevel;
	}
): void {
	const dedupeKey =
		options?.dedupeKey ??
		`${eventName}:${String(payload.status ?? '')}:${String(payload.endpoint ?? '')}:${String(payload.error_code ?? '')}`;
	if (shouldDedupe(dedupeKey)) {
		return;
	}

	const level = options?.level ?? inferLogLevel(typeof payload.status === 'number' ? payload.status : undefined);

	Sentry.withScope(scope => {
		scope.setTag('event_name', eventName);
		scope.setTag('signal_type', 'structured_log');
		scope.setLevel?.(level);
		applyPayload(scope, payload);
		Sentry.captureMessage(eventName, level);
	});
}

export function captureIssue(error: unknown, eventName: ObservabilityEventName, payload?: ObservabilityPayload): void {
	Sentry.withScope(scope => {
		scope.setTag('event_name', eventName);
		scope.setTag('signal_type', 'issue');
		if (payload !== undefined) {
			applyPayload(scope, payload);
		}
		Sentry.captureException(error);
	});
}
